import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../hooks/useAuth';
import { API_BASE_URL } from '../config';
import { PageHeader } from '../components/PageHeader';
import { NimbusTable } from '../components/NimbusTable';

interface MonthRow {
  yearMonth: string;
  inputTokens: number;
  outputTokens: number;
  callCount: number;
  estimatedCostUsd: number;
}

interface SummaryResponse {
  currentMonth: MonthRow;
  previousMonth: MonthRow;
  mostExpensiveFeature: { feature: string; estimatedCostUsd: number } | null;
  pricingAsOf: string;
}

interface RecentCall {
  timestamp: string;
  feature: string;
  modelId: string;
  inputTokens: number;
  outputTokens: number;
  estimatedCostUsd: number;
  status: string;
}

function authHeaders(): Record<string, string> {
  const token = localStorage.getItem('idToken') || '';
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
}

async function fetchJson<T>(path: string): Promise<T> {
  const res = await fetch(`${API_BASE_URL}${path}`, { headers: authHeaders() });
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
  return res.json();
}

const MONTH_OPTIONS = [
  { value: 3, label: 'Last 3 months' },
  { value: 6, label: 'Last 6 months' },
  { value: 12, label: 'Last 12 months' },
];

const formatUsd = (v: number) => `$${v.toFixed(2)}`;
const formatTokens = (v: number) => v.toLocaleString();

export const AiUsagePage: React.FC = () => {
  const { user } = useAuth();
  // Must match App.tsx's isTenantAdmin expression verbatim — the "AI Usage & Spend"
  // nav entry and this page's own gate need to agree, unlike /admin's AdminPortalPage,
  // whose nav gate (isTenantAdmin) and page gate (isPlatformAdmin) are two different
  // flags, so a tenant admin who clicks the link hits "Access denied."
  const isTenantAdmin = (user?.role || '').toLowerCase() === 'admin';
  const [months, setMonths] = useState(6);

  const { data: summary, error: summaryError } = useQuery<SummaryResponse>({
    queryKey: ['ai-usage-summary'],
    queryFn: () => fetchJson('/ai-usage/summary'),
    enabled: isTenantAdmin,
    staleTime: 30_000,
  });

  const { data: byPeriod, isLoading: byPeriodLoading } = useQuery<{ months: MonthRow[] }>({
    queryKey: ['ai-usage-by-period', months],
    queryFn: () => fetchJson(`/ai-usage/by-period?months=${months}`),
    enabled: isTenantAdmin,
    staleTime: 30_000,
  });

  const { data: recentCalls, isLoading: recentLoading } = useQuery<{ calls: RecentCall[] }>({
    queryKey: ['ai-usage-recent-calls'],
    queryFn: () => fetchJson('/ai-usage/recent-calls?limit=20'),
    enabled: isTenantAdmin,
    staleTime: 30_000,
  });

  if (!isTenantAdmin) {
    return (
      <div className="container-xxl flex-grow-1 container-p-y np-page">
        <div className="np-banner np-tone-danger">
          <i className="ri-error-warning-line"></i>
          <span>Unauthorized: this page is only accessible to your organisation's admins.</span>
        </div>
      </div>
    );
  }

  const totalTokens = summary ? summary.currentMonth.inputTokens + summary.currentMonth.outputTokens : 0;

  return (
    <div className="container-xxl flex-grow-1 container-p-y np-page">
      <PageHeader
        icon="ri-coin-line"
        title="AI Usage & Spend"
        subtitle="Token usage and estimated cost across AI features — no need to check AWS directly."
      />

      <div className="np-grid np-grid-4">
        <div className="np-card">
          <div className="np-card-body np-stat">
            <span className="np-stat-icon np-tone-primary"><i className="ri-coin-line"></i></span>
            <p className="np-stat-label">This Month's Spend</p>
            <p className="np-stat-value np-tabular">{summary ? formatUsd(summary.currentMonth.estimatedCostUsd) : '—'}</p>
            {summary && <p className="np-hint mb-0">Estimated — pricing as of {summary.pricingAsOf}</p>}
          </div>
        </div>
        <div className="np-card">
          <div className="np-card-body np-stat">
            <span className="np-stat-icon np-tone-info"><i className="ri-cpu-line"></i></span>
            <p className="np-stat-label">Total Tokens</p>
            <p className="np-stat-value np-tabular">{formatTokens(totalTokens)}</p>
            {summary && (
              <p className="np-hint mb-0">
                {formatTokens(summary.currentMonth.inputTokens)} in · {formatTokens(summary.currentMonth.outputTokens)} out
              </p>
            )}
          </div>
        </div>
        <div className="np-card">
          <div className="np-card-body np-stat">
            <span className="np-stat-icon np-tone-success"><i className="ri-flashlight-line"></i></span>
            <p className="np-stat-label">AI Calls This Month</p>
            <p className="np-stat-value np-tabular">{summary ? summary.currentMonth.callCount : '—'}</p>
          </div>
        </div>
        <div className="np-card">
          <div className="np-card-body np-stat">
            <span className="np-stat-icon np-tone-warning"><i className="ri-alert-line"></i></span>
            <p className="np-stat-label">Most Expensive Feature</p>
            <p className="np-stat-value" style={{ fontSize: '1rem' }}>
              {summary?.mostExpensiveFeature ? summary.mostExpensiveFeature.feature : '—'}
            </p>
            {summary?.mostExpensiveFeature && (
              <p className="np-hint mb-0">{formatUsd(summary.mostExpensiveFeature.estimatedCostUsd)} this month</p>
            )}
          </div>
        </div>
      </div>

      {summaryError && (
        <div className="np-banner np-tone-danger">
          <i className="ri-error-warning-line"></i>
          <span>Failed to load usage summary: {(summaryError as Error).message}</span>
        </div>
      )}

      <div className="np-card">
        <div className="np-card-head">
          <h2>Monthly Breakdown</h2>
        </div>
        <div className="np-card-body border-bottom">
          <div className="d-flex gap-2 flex-wrap align-items-center">
            <select className="np-select" style={{ maxWidth: 180 }} value={months} onChange={(e) => setMonths(Number(e.target.value))}>
              {MONTH_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            {summary && <span className="np-hint ms-auto mb-0">Pricing as of {summary.pricingAsOf} — estimate, not a billing statement</span>}
          </div>
        </div>
        <div className="np-card-body p-0">
          {byPeriodLoading && (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status" />
            </div>
          )}
          {!byPeriodLoading && (
            <NimbusTable
              thead={
                <tr>
                  <th>Month</th>
                  <th>Input Tokens</th>
                  <th>Output Tokens</th>
                  <th>Calls</th>
                  <th>Est. Cost</th>
                </tr>
              }
              isEmpty={!byPeriod?.months.length}
              emptyState={<div className="text-center np-sub py-4">No AI usage recorded yet.</div>}
            >
              {(byPeriod?.months || []).map((row) => (
                <tr key={row.yearMonth}>
                  <td className="np-tabular">{row.yearMonth}</td>
                  <td className="np-tabular">{formatTokens(row.inputTokens)}</td>
                  <td className="np-tabular">{formatTokens(row.outputTokens)}</td>
                  <td className="np-tabular">{row.callCount}</td>
                  <td className="np-tabular">{formatUsd(row.estimatedCostUsd)}</td>
                </tr>
              ))}
            </NimbusTable>
          )}
        </div>
      </div>

      <div className="np-card">
        <div className="np-card-head">
          <h2>Recent Calls</h2>
        </div>
        <div className="np-card-body p-0">
          {recentLoading && (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status" />
            </div>
          )}
          {!recentLoading && (
            <NimbusTable
              thead={
                <tr>
                  <th>Time</th>
                  <th>Feature</th>
                  <th>Model</th>
                  <th>Tokens</th>
                  <th>Est. Cost</th>
                  <th>Status</th>
                </tr>
              }
              isEmpty={!recentCalls?.calls.length}
              emptyState={<div className="text-center np-sub py-4">No AI calls recorded yet.</div>}
            >
              {(recentCalls?.calls || []).map((call, idx) => (
                <tr key={idx}>
                  <td className="np-tabular np-hint">{call.timestamp ? new Date(call.timestamp).toLocaleString() : '—'}</td>
                  <td>{call.feature}</td>
                  <td><code className="small">{call.modelId}</code></td>
                  <td className="np-tabular">{formatTokens(call.inputTokens + call.outputTokens)}</td>
                  <td className="np-tabular">{formatUsd(call.estimatedCostUsd)}</td>
                  <td>
                    <span className={`np-chip np-tone-${call.status === 'success' ? 'success' : 'danger'}`}>
                      {call.status === 'success' ? 'Success' : 'Failed'}
                    </span>
                  </td>
                </tr>
              ))}
            </NimbusTable>
          )}
        </div>
      </div>
    </div>
  );
};

export default AiUsagePage;
